import toast from "react-hot-toast";
import { useEffect, useMemo, useRef, useState } from "react";
import { useRouter } from "@/app/hooks/useRouter";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useNavigate, useParams } from "react-router";
import { useReactToPrint } from "react-to-print";
import CartCreationTableComponent from "../CartCreationTableComponent";
import { useModalActions } from "@/app/hooks/ui_hooks/useModalActions";
import NudgesContainer from "@/app/components/ui/Nudges/NudgesContainer";
import CartCreationHeaderComponent from "../CartCreationHeaderComponent";
import { useCartListAPI } from "@/app/hooks/api_hooks/Cart/useCartListAPI";
import CartStockIncompleteComponent from "../CartStockIncompleteComponent";
import { useAppDispatch, useAppSelector } from "@/app_redux/hooks/root_hook";
import { useEMRdownloadPDF } from "@/app/hooks/api_hooks/emr/useEMRdownloadPDF";
import { useCartCalloutAPI } from "@/app/hooks/api_hooks/Cart/useCartCalloutAPI";
import { useCartDetailsAPI } from "@/app/hooks/api_hooks/Cart/useCartDetailsAPI";
import { useCartItemDeleteAPI } from "@/app/hooks/api_hooks/Cart/useCartItemDeleteAPI";
import { IClickLogBody, useClickLogAPI } from "@/app/hooks/api_hooks/org/useClickLogAPI";
import { useUpdateCartDetailsAPI } from "@/app/hooks/api_hooks/Cart/useUpdateCartDetailsAPi";
import { ICustomerProfileState, setPetId } from "@/app_redux/reducers/slices/auth/customer_profile_slice";
import { ICartCheckout, useCartCheckoutAPI } from "@/app/hooks/api_hooks/Cart/useCartCheckoutAPI";
import { useClinicAvailableParavetsAPI } from "@/app/hooks/api_hooks/Cart/useClinicAvailableParavetsAPI";
import CartParavetSelectCard from "./components/CartParavetSelectCard";
import { useCartAppointmentCommentsAPI } from "@/app/hooks/api_hooks/Cart/useCartAppointmentCommentsAPI";
import { ICartAddressDetails, ICartDetailsPetCartItemData, ICartFilterBody } from "@/api_framework/api_modals/Cart";
import { useCartSearchItemClickAPI } from "@/app/hooks/api_hooks/Cart/useCartSearchItemClickAPI";
import {
  ICartState,
  setActiveCartId,
  setCartLevelCouponId,
  setInoviceList,
} from "@/app_redux/reducers/slices/auth/cart_slice";
import { setLoading } from "@/app_redux/reducers/loading_reducer";
import { useUtils } from "@/app/hooks/useUtils";
import { useAddressListAPI } from "@/app/hooks/api_hooks/HDCart/useAddressListAPI";
import { useHDAddressClusterChangeAPI } from "@/app/hooks/api_hooks/HDCart/useHDAddressClusterChangeAPI";
import AddressClusterChangeModal from "../AddressClusterChangeModal";
import { useLoader } from "@/app/hooks/useLoader";
import PetUpdateModal from "@/app/components/ui/Modals/PetUpdateModel";
import MirrorScreenActions from "../MirrorScreenActions";
import { useRecentEMRAPI } from "@/app/hooks/api_hooks/emr/useRecentEMRAPI";
import { useUserMembershipsAPI } from "@/app/hooks/api_hooks/Cart/useUserMembershipAPI";
import { useMembershipPitchDetailsAPI } from "@/app/hooks/api_hooks/Cart/useMembershipPitchDetailsAPI";
import { useJobCardEligibilityAPI } from "@/app/hooks/api_hooks/Cart/useJobCardEligibilityAPI";
import "./cart-print.css";
import { useVerifyUserProfilePinAPI } from "@/app/hooks/api_hooks/PWL/useVerifyUserProfilePinAPI";
import { useCartPinFlowState } from "./hooks/useCartPinFlowState";
import { useBarcodeValidationState } from "./hooks/useBarcodeValidationState";
import { useAddressSelectionState } from "./hooks/useAddressSelectionState";
import { useCheckoutFlowState } from "./hooks/useCheckoutFlowState";
import { useMirrorScreenState } from "./hooks/useMirrorScreenState";
import { useCartLotDetailsFlow } from "./hooks/useCartLotDetailsFlow";
import { useCartMirrorPresentation } from "./hooks/useCartMirrorPresentation";
import CartHeaderActionsBar from "./components/CartHeaderActionsBar";
import CartCommentsCard from "./components/CartCommentsCard";
import CartUserPinCard from "./components/CartUserPinCard";
import CartJobCardEligibilityCard from "./components/CartJobCardEligibilityCard";
import CartPriceBreakupPanel from "./components/CartPriceBreakupPanel";
import CartControlCenter from "./components/CartControlCenter";
import CartModalsCluster from "./components/CartModalsCluster";
import { useMirrorPresenceStatus } from "@/app/hooks/useMirrorPresenceStatus";
import CartDetailsModal from "../CartDetailsModal";
import FollowupBookingCreation from "../CartBillingComponent/FollowupBookingCreation";
import { useFeatureAccess } from "@/app/hooks/useFeatureAccess";
import { NO_PARAVET_ID } from "@/constants";

export interface ICartOutOfStockListing {
  petId: string;
  petName: string;
  outOfStockItems: ICartDetailsPetCartItemData[];
}

const CartCreationComponent = () => {
  const params = useParams();
  const { getRouteKey } = useRouter();
  const { execute } = useCartCalloutAPI();
  const { activeCartDetails, cartLevelCouponId, walletDetails } = useRouteInfo(getRouteKey("CART_MANAGEMENT", "id"))
    .routeState.state as ICartState;
  const rewardCreditBalance = Number(walletDetails?.wallets?.marketing_credit || 0);
  const mappedClinicId = useAppSelector((state) => state.root.UIStates.mappedClinicId);
  const { data: comments, personaDiscountComments, execute: commentsApi } = useCartAppointmentCommentsAPI();
  const { execute: fetchCartDetails } = useCartDetailsAPI();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const {
    activeModal,
    setActiveModal,
    setActiveModalWithClusterFlag,
    selectedAddress,
    setSelectedAddress,
    selectedAddressId,
    setSelectedAddressId,
    currentAddress,
    setCurrentAddress,
    pendingAddress,
    setPendingAddress,
    clusterResponse: _clusterResponse,
    setClusterResponse,
    showClusterChangeModal,
    setShowClusterChangeModal,
  } = useAddressSelectionState();
  const {
    isCheckoutModalOpen: open,
    setIsCheckoutModalOpen: setOpen,
    checkOutLoader,
    setCheckOutLoader,
    checkoutNudge,
    setCheckoutNudge,
  } = useCheckoutFlowState();
  // RBAC: CART_CREATION_CLINIC_MODE — legacy roles: clinic_ops, clinic_ops_doggyworld
  const isClinicOpsUser = useFeatureAccess("CART_CREATION_CLINIC_MODE");
  const userEmailId = useAppSelector((state) => state.auth?.user?.email);
  // RBAC: CART_CREATION_DOCTOR_MODE — legacy role: doctor
  const isDoctorMode = useFeatureAccess("CART_CREATION_DOCTOR_MODE");
  const {
    execute: fetchProductDetails,
    retry,
    alreadyAddedURIDError,
    isPetModelOpen,
    setIsPetModelOpen,
    errorSource,
  } = useCartSearchItemClickAPI();
  const { execute: updateCart } = useUpdateCartDetailsAPI();
  const { execute: refetchCartDetails } = useCartDetailsAPI();
  const { execute: deleteCartItem } = useCartItemDeleteAPI();
  const { execute: checkoutOrder } = useCartCheckoutAPI();
  const {
    execute: fetchAvailableParavets,
    paravetList,
    isLoading: isParavetListLoading,
  } = useClinicAvailableParavetsAPI();
  const [selectedParavetId, setSelectedParavetId] = useState<string>("");
  const { execute: verifyUserProfilePin } = useVerifyUserProfilePinAPI();
  const { execute: generateEmrPDf } = useEMRdownloadPDF();
  const { execute: fetchCartList } = useCartListAPI();
  const { activeCartId, addressList } = useRouteInfo(getRouteKey("CART_MANAGEMENT", "id"))?.routeState
    ?.state as ICartState;
  const { petId, customerId, customerDetails, petDetails } = useRouteInfo(getRouteKey("CUSTOMER_PROFILE", "id"))
    .routeState.state as ICustomerProfileState;
  const { mappedClinicName } = useAppSelector((state) => state.root.UIStates);
  const { execute: logAPI } = useClickLogAPI();
  const [loader, setLoader] = useState(false);
  const [isFollowUpModalOpen, setIsFollowUpModalOpen] = useState<boolean>(false);
  const [isCartDetailsModalOpen, setIsCartDetailsModalOpen] = useState<boolean>(false);
  const [useRewardCredit, setUseRewardCredit] = useState<boolean>(true);
  const [hasAutoOpenedFollowUp, setHasAutoOpenedFollowUp] = useState<boolean>(false);
  const [initialLoad, setInitialLoad] = useState(false);
  const [initialCartLoad, setInitialCartLoad] = useState(false);
  const alert_category_list = ["Discount Check", "Invoicing Error", "day_closing"];
  const { veticDateFormat } = useUtils();
  const availableHdItems =
    activeCartDetails?.hd_cart?.main_cart_items?.filter((item) => item.availability !== "unavailable") || [];
  const fetchAddressList = useAddressListAPI();
  const { execute: fetchAddressCluster } = useHDAddressClusterChangeAPI();
  const selectedDeliveryAddresses = addressList?.filter((addr) => addr?.is_selected === true);
  const isCartTabeLoading = useLoader("isCustomerCartTableInProgress");
  const isHDCartTabeLoading = useLoader("isCustomerHDCartTableInProgress");
  const hasNormalCartItems = activeCartDetails?.pets?.some((pet) => pet.items?.length > 0) ?? false;
  const hasHdCartItems = (activeCartDetails?.hd_cart?.main_cart_items?.length ?? 0) > 0;
  const showCheckoutButton = hasNormalCartItems || hasHdCartItems;
  const cmbookingRecommendations = activeCartDetails?.cm_booking_recommendations;

  /* The cart can be opened without a pet_id in the URL (cart list, EMR "Go to Cart"),
     in which case the store still holds whichever pet was selected earlier - usually the
     customer's first pet. Align the selection with the pet the cart actually holds items
     for, once per cart, so the pet dropdown in the header matches the cart contents. */
  const petSyncedCartIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!activeCartId || !activeCartDetails?.pets?.length) return;
    if (petSyncedCartIdRef.current === activeCartId) return;
    const petsWithItems = activeCartDetails.pets.filter((pet) => (pet?.items?.length ?? 0) > 0);
    if (!petsWithItems.length) return;
    petSyncedCartIdRef.current = activeCartId;
    if (petsWithItems.some((pet) => pet?.pet_id === petId)) return;
    dispatch(setPetId({ petId: petsWithItems[0]?.pet_id }));
    localStorage.setItem("selectedPetId", petsWithItems[0]?.pet_id);
  }, [activeCartId, activeCartDetails?.pets, petId]);
  useEffect(() => {
    if (!isCartTabeLoading && !hasAutoOpenedFollowUp && (cmbookingRecommendations?.length ?? 0) > 0) {
      setHasAutoOpenedFollowUp(true);
      setIsFollowUpModalOpen(true);
    }
  }, [isCartTabeLoading, cmbookingRecommendations, hasAutoOpenedFollowUp]);
  const {
    isMirrorActionVisible,
    setIsMirrorActionVisible,
    recentEMRData,
    setRecentEMRData,
    membershipData,
    setMembershipData,
    pitchingData,
    setPitchingData,
    initialRender,
    setInitialRender,
  } = useMirrorScreenState();
  const { setVisible, setModalData } = useModalActions("CART_ITEM_ADD_MODAL");
  const { setVisible: openDocumentUploadModal, setModalData: setDocumentUploadModalData } = useModalActions(
    "CERTIFICATE_DOCUMENT_UPLOAD_MODAL",
  );
  const [openMembershipExemption, setOpenMembershipExemption] = useState(false);
  const {
    isBarcodeSlideoverOpen,
    barcodeInputValue,
    showBarcodeMismatchError,
    pendingBarcodeValidation,
    setBarcodeInputValue,
    setShowBarcodeMismatchError,
    openBarcodeValidation: openBarcodeValidationSlideover,
    closeBarcodeValidation: closeBarcodeValidationSlideover,
  } = useBarcodeValidationState();
  const {
    cartUserPinDigits,
    setCartUserPinDigits,
    isCartPinVerified,
    setIsCartPinVerified,
    isCartPinExempted,
    setIsCartPinExempted,
    isCartPinVerifying,
    setIsCartPinVerifying,
    openCartPinExemptionModal,
    setOpenCartPinExemptionModal,
    isCartPinSendModalOpen,
    setIsCartPinSendModalOpen,
    resetCartPinFlow,
  } = useCartPinFlowState();
  const cartPinInputRefs = useRef<Array<HTMLInputElement | null>>([]);
  const fetchCustomerAPI = useRecentEMRAPI();
  const fetchMemberships = useUserMembershipsAPI();
  const fetchPitchDetails = useMembershipPitchDetailsAPI();
  const { data: jobCardEligibility, execute: fetchJobCardEligibility } = useJobCardEligibilityAPI();
  const lastEligibilityRequestKeyRef = useRef<string | null>(null);
  const printContainerRef = useRef<HTMLDivElement>(null);

  const handlePrintCart = useReactToPrint({
    contentRef: printContainerRef,
    documentTitle: `cart-${activeCartId ?? "details"}`,
  });

  const isCheckoutDisabled = (() => {
    if (!showCheckoutButton) return true;
    if (hasNormalCartItems && !hasHdCartItems) return false;
    return !currentAddress || !selectedDeliveryAddresses?.length;
  })();
  const shouldShowParavetSection = activeCartDetails?.is_paravet_required === true;
  const isParavetGateCleared = !shouldShowParavetSection || Boolean(selectedParavetId);
  const shouldShowCartPinSection = activeCartDetails?.require_user_profile_pin === true;
  const isCartPinComplete = cartUserPinDigits.every((digit) => /^\d$/.test(digit));
  const isCartPinGateCleared = !shouldShowCartPinSection || isCartPinVerified || isCartPinExempted;
  // Keep checkout disabled while cart details are being (re)fetched: until the API responds we
  // can't trust require_user_profile_pin (it's stale/undefined), so the PIN gate must fail closed.
  const isCartDetailsLoading = isCartTabeLoading || isHDCartTabeLoading;
  const finalCheckoutDisabled =
    isCheckoutDisabled || !isCartPinGateCleared || !isParavetGateCleared || isCartDetailsLoading;
  const selectedPinBookingId =
    activeCartDetails?.profile_pin_eligible_booking_id ??
    activeCartDetails?.pets?.flatMap((pet) => pet?.items ?? [])?.find((item) => item?.booking_id)?.booking_id;

  useEffect(() => {
    if (shouldShowParavetSection && mappedClinicId) {
      fetchAvailableParavets(mappedClinicId);
    }
  }, [shouldShowParavetSection, mappedClinicId, fetchAvailableParavets]);

  useEffect(() => {
    if (!shouldShowParavetSection) {
      setSelectedParavetId("");
      return;
    }
    if (selectedParavetId && selectedParavetId !== NO_PARAVET_ID &&
      !paravetList.some((paravet) => paravet.paravet_id === selectedParavetId)) {
      setSelectedParavetId("");
    }
  }, [shouldShowParavetSection, paravetList, selectedParavetId]);

  const handleCartPinChange = (index: number, value: string) => {
    const sanitizedValue = value.replace(/\D/g, "").slice(-1);
    setCartUserPinDigits((prev) => {
      const updated = [...prev];
      updated[index] = sanitizedValue;
      return updated;
    });
    if (sanitizedValue && index < 3) {
      cartPinInputRefs.current[index + 1]?.focus();
    }
    if (isCartPinVerified) setIsCartPinVerified(false);
    if (isCartPinExempted) setIsCartPinExempted(false);
  };

  const handleCartPinKeyDown = (index: number, key: string) => {
    if (key !== "Backspace") return;
    if (cartUserPinDigits[index]) {
      setCartUserPinDigits((prev) => {
        const updated = [...prev];
        updated[index] = "";
        return updated;
      });
      return;
    }
    if (index > 0) {
      setCartUserPinDigits((prev) => {
        const updated = [...prev];
        updated[index - 1] = "";
        return updated;
      });
      cartPinInputRefs.current[index - 1]?.focus();
    }
  };

  const handleVerifyCartPin = async () => {
    if (!isCartPinComplete || isCartPinVerifying) return;
    if (!customerId || !mappedClinicId) {
      toast.error("Customer or clinic details are missing");
      return;
    }
    if (!selectedPinBookingId) {
      toast.error("Booking ID missing for PIN verification");
      return;
    }
    setIsCartPinVerifying(true);
    logCartAction("CART_PIN_VERIFY_CLICK", { booking_id: selectedPinBookingId });
    const response = await verifyUserProfilePin({
      pin: cartUserPinDigits.join(""),
      user_id: customerId,
      clinic_id: mappedClinicId,
      booking_id: selectedPinBookingId,
      intent: "checkout",
      source: "VMS",
      created_by: "CM",
    });
    setIsCartPinVerifying(false);
    logCartAction("CART_PIN_VERIFY_RESULT", {
      booking_id: selectedPinBookingId,
      event_action: response?.status ? "success" : "failed",
    });
    if (response?.status) {
      setIsCartPinVerified(true);
      setIsCartPinExempted(false);
      toast.success(response?.message ?? "PIN verified successfully");
      if (activeCartId) {
        await refetchCartDetails(
          activeCartId,
          mappedClinicId,
          "normal",
          cartLevelCouponId,
          currentAddress?.pincode,
          undefined,
          true,
        );
      }
      return;
    }
    setIsCartPinVerified(false);
  };

  const handleSendCartPin = () => {
    logCartAction("CART_PIN_SEND_CLICK", { booking_id: selectedPinBookingId });
    if (!customerId || !customerDetails?.mobile) {
      toast.error("Customer phone number is missing");
      return;
    }
    setIsCartPinSendModalOpen(true);
  };

  const handleCartPinSendSuccess = () => {
    // Sending the PIN/OTP only dispatches it to the customer — it must NOT clear the
    // checkout gate. Keep it closed so the CM still has to enter and verify the PIN.
    setIsCartPinExempted(false);
    setIsCartPinVerified(false);
  };

  const outOfStockItems: ICartOutOfStockListing[] = activeCartDetails
    ? activeCartDetails?.pets
      ?.map((pet) => {
        return {
          petId: pet.pet_id,
          petName: pet.pet_name,
          outOfStockItems: pet.out_of_stock_items,
        };
      })
      .flat()
    : [];

  const eligibilityTriggerKey = useMemo(() => {
    if (!activeCartId) return "";
    const itemSignature = (activeCartDetails?.pets ?? [])
      .flatMap((pet) =>
        (pet?.items ?? []).map(
          (item) => `${pet?.pet_id}:${item?.id}:${item?.unique_row_id}:${item?.qty}:${item?.booking_id ?? ""}`,
        ),
      )
      .sort()
      .join("|");
    return `${activeCartId}::${itemSignature}`;
  }, [activeCartId, activeCartDetails?.pets]);

  const handleOpenExemptionModal = () => {
    logCartAction("CART_RAISE_EXEMPTION_CLICK");
    if (!customerId || !mappedClinicId) {
      toast.error("Customer or clinic information missing");
      return;
    }
    setOpenMembershipExemption(true);
  };

  const handleOpenJobCardUploadModal = (pet: { id: string; name: string }) => {
    const bookingIdForPet = activeCartDetails?.pets
      ?.find((p) => p?.pet_id === pet?.id)
      ?.items?.map((item) => item?.booking_id)
      ?.find((id) => Boolean(id));
    logCartAction("CART_JOB_CARD_UPLOAD_CLICK", { pet_id: pet?.id, booking_id: bookingIdForPet });

    setDocumentUploadModalData({
      booking_id: bookingIdForPet,
      customer_id: customerId,
      user_details: customerDetails ? { id: customerId, name: customerDetails?.name } : { id: customerId },
      pet_details: { id: pet?.id, name: pet?.name },
      clinic_details: { id: mappedClinicId, name: mappedClinicName },
      document_types: ["Job Card"],
      source: "cart_job_card",
      onUploadSuccess: async () => {
        refetchCartList();
        if (activeCartId) {
          await refetchCartDetails(activeCartId, mappedClinicId, "hd", cartLevelCouponId, currentAddress?.pincode);
        }
      },
    });
    openDocumentUploadModal();
  };

  useEffect(() => {
    localStorage.setItem("lastPresentedCartId", params.cart_id ?? "null");
  }, [params.cart_id]);

  useEffect(() => {
    if (customerId) {
      fetchAddressList(customerId, null, null, mappedClinicId);
    }
  }, [customerId]);

  useEffect(() => {
    if (addressList?.length > 0) {
      const selected = addressList.find((addr) => addr.is_selected) || addressList[0];
      setCurrentAddress(selected);
    }
  }, [addressList]);

  useEffect(() => {
    if (currentAddress && activeCartId) {
      fetchCartDetails(params.cart_id, mappedClinicId, "hd", cartLevelCouponId, currentAddress.pincode);
    }
  }, [currentAddress, activeCartId]);

  useEffect(() => {
    if (params.user_id) {
      execute(params.user_id);
      commentsApi(params.user_id);
    }
  }, []);

  useEffect(() => {
    if (params.cart_id) {
      dispatch(setActiveCartId(params.cart_id));
      dispatch(setInoviceList([]));
    }
  }, [params.cart_id]);

  useEffect(() => {
    resetCartPinFlow();
  }, [activeCartId]);

  const refetchCartList = () => {
    const filterBody: ICartFilterBody = {
      cart_status: ["Unbilled"],
      start_date: null,
      end_date: null,
    };
    fetchCartList(filterBody, params.user_id);
    dispatch(setInoviceList([]));
  };

  const handleExemptionRefresh = async () => {
    refetchCartList();
    if (!activeCartId) return;
    await refetchCartDetails(
      activeCartId,
      mappedClinicId,
      "normal",
      cartLevelCouponId,
      currentAddress?.pincode,
      undefined,
      true,
    );
  };

  useEffect(() => {
    refetchCartList();
  }, []);

  useEffect(() => {
    if (addressList?.length > 0) {
      const oldSelected = addressList.find((addr) => addr.is_selected);
      setCurrentAddress(oldSelected || null);
    }
  }, [addressList]);

  useEffect(() => {
    if (activeCartId) {
      refetchCartDetails(activeCartId, mappedClinicId, "hd", cartLevelCouponId, currentAddress?.pincode);
      callLogAPILabSelectedAddress(currentAddress?.address_id);
    }
  }, [activeCartId, currentAddress?.pincode]);

  useEffect(() => {
    if (!activeCartId || !eligibilityTriggerKey) return;
    if (lastEligibilityRequestKeyRef.current === eligibilityTriggerKey) return;
    lastEligibilityRequestKeyRef.current = eligibilityTriggerKey;
    fetchJobCardEligibility(activeCartId);
  }, [activeCartId, eligibilityTriggerKey, fetchJobCardEligibility]);

  useEffect(() => {
    const handleCartJobCardUploaded = () => {
      if (!activeCartId) return;
      lastEligibilityRequestKeyRef.current = null;
      fetchJobCardEligibility(activeCartId);
    };

    window.addEventListener("cart:job-card-uploaded", handleCartJobCardUploaded);
    return () => {
      window.removeEventListener("cart:job-card-uploaded", handleCartJobCardUploaded);
    };
  }, [activeCartId, fetchJobCardEligibility]);

  const handleSetActiveModal = (modalName: "addressList" | "addAddress" | "addressClusterChange" | null) => {
    setActiveModalWithClusterFlag(modalName);
  };

  const { fetchLotDetails, handleBarcodeValidateAndAdd, handlePetUpdateRetrySuccess } = useCartLotDetailsFlow({
    activeCartId,
    mappedClinicId,
    customerId,
    petId,
    currentAddress,
    activeCartDetails,
    cartLevelCouponId,
    isClinicOpsUser,
    fetchProductDetails,
    updateCart,
    deleteCartItem,
    refetchCartDetails,
    setModalData,
    setVisible,
    pendingBarcodeValidation,
    barcodeInputValue,
    isBarcodeSlideoverOpen,
    setShowBarcodeMismatchError,
    openBarcodeValidationSlideover,
    closeBarcodeValidationSlideover,
  });

  const handleApplyCartLevelCoupon = async (couponId: string) => {
    await refetchCartDetails(activeCartId, mappedClinicId, "normal", couponId, currentAddress?.pincode).then(() => {
      toast.success("Cart Level Coupon Applied Successfully.");
      dispatch(setCartLevelCouponId(couponId));
    });
  };

  const handleRemoveCartLevelCoupon = async () => {
    await refetchCartDetails(activeCartId, mappedClinicId, "normal", "remove", currentAddress?.pincode).then((res) => {
      if (res) {
        toast.success("Cart Level Coupon Removed Successfully.");
        dispatch(setCartLevelCouponId(null));
      }
    });
  };

  const handelRefreschCartItems = async () => {
    setLoader(true);
    // The "normal" refetch only drives the normal table's loader, so also flag the HD
    // table as loading here to keep both tables in a loading state during Sync EMR.
    dispatch(setLoading({ key: "isCustomerHDCartTableInProgress", status: true }));
    await refetchCartDetails(
      activeCartId,
      mappedClinicId,
      "normal",
      cartLevelCouponId,
      currentAddress?.pincode,
      "showMessage",
    ).finally(() => {
      setLoader(false);
      dispatch(setLoading({ key: "isCustomerHDCartTableInProgress", status: false }));
    });
  };

  useEffect(() => {
    if (activeCartDetails?.emr_id && !initialLoad) {
      const logBody: IClickLogBody = {
        event_type: "EMR_TO_CART_DATA",
        scr_id: "EMR",
        client_id: customerId,
        emr_id: activeCartDetails?.emr_id,
        cart_id: activeCartId,
        pet_id: petId,
        emr_cart_items: activeCartDetails,
        clinic_id: mappedClinicId,
        clinic_name: mappedClinicName,
        vms_user_id: userEmailId,
        timestamp: Date.now(),
      };
      logAPI(logBody);
      setInitialLoad(true);
    }
  }, [initialLoad, activeCartDetails]);

  const callLogAPIBase = (
    item: any,
    event_type: "EMR_CART_CHECKOUT" | "CART_SELECTED_ADDRESS" | "CART_CHECKOUT_DETAILS",
  ) => {
    const logBody: IClickLogBody = {
      event_type: event_type,
      scr_id: "EMR",
      client_id: customerId,
      emr_id: activeCartDetails?.emr_id,
      cart_id: activeCartId,
      pet_id: petId,
      emr_cart_items: item,
      address_id: item,
      clinic_id: mappedClinicId,
      clinic_name: mappedClinicName,
      vms_user_id: userEmailId,
      timestamp: Date.now(),
    };
    logAPI(logBody);
  };

  const logCartAction = (event_type: string, extra: Partial<IClickLogBody> = {}) => {
    logAPI({
      event_type,
      scr_id: "CART",
      client_id: customerId,
      pet_id: petId,
      cart_id: activeCartId,
      emr_id: activeCartDetails?.emr_id,
      clinic_id: mappedClinicId,
      clinic_name: mappedClinicName,
      vms_user_id: userEmailId,
      timestamp: Date.now(),
      ...extra,
    });
  };

  const callLogAPILabSelectedAddress = (item: any) => callLogAPIBase(item, "CART_SELECTED_ADDRESS");
  const callLogAPILabCheckout = (item: any) => callLogAPIBase(item, "CART_CHECKOUT_DETAILS");

  const handelCheckoutFunction = async (isNudgeOpend?: boolean, CTA?: string) => {
    if (!isNudgeOpend && hasHdCartItems && (CTA === "CTA1" || CTA === "CT")) {
      setCheckoutNudge(false);
      setOpen(true);
      return;
    }
    if (hasNormalCartItems && !hasHdCartItems && !isNudgeOpend && (CTA === "CTA1" || CTA === "CT")) {
      try {
        setCheckOutLoader(true);
        const constructedBody: ICartCheckout = {
          cart_id: activeCartId,
          clinic_id: mappedClinicId,
          ...(selectedParavetId ? { paravet_id: selectedParavetId } : {}),
          use_reward_credit: useRewardCredit,
        };
        const { status, surgePricesChanged } = (await checkoutOrder(constructedBody)) ?? {};
        if (surgePricesChanged && activeCartId) {
          await refetchCartDetails(activeCartId, mappedClinicId, "normal", cartLevelCouponId, currentAddress?.pincode);
        }
        if (status) {
          callLogAPILabCheckout(activeCartDetails);
          const targetUrl = `/app/user-cart/cart-billing/${customerId}?from_cart=true`;
          setCheckOutLoader(false);
          navigate(targetUrl);
          return;
        } else {
          setCheckoutNudge(false);
          console.error("Checkout failed: status is false");
        }
      } catch (error) {
        setCheckoutNudge(false);
        console.error("Checkout failed:", error);
      } finally {
        setCheckOutLoader(false);
      }
    }
  };

  const viewEMRPDF = (emr: string) => {
    if (isDoctorMode) {
      navigate(`/app/customer/profile/customer-details/EMR/${customerId}/${petId}`);
    } else {
      generateEmrPDf(emr);
    }
  };

  const handleCheckoutmodal = () => {
    logCartAction("CART_CHECKOUT_CLICK", { staff_id: selectedParavetId || undefined });
    setIsCartDetailsModalOpen(true);
  };

  const handleCartDetailsContinue = () => {
    setIsCartDetailsModalOpen(false);
    setCheckoutNudge(true);
  };

  const handleAddressChange = async (newAddress: ICartAddressDetails) => {
    const oldAddressId = currentAddress?.address_id;
    const newAddressId = newAddress.address_id;
    const response = await fetchAddressCluster(newAddressId, oldAddressId, customerId);
    setClusterResponse(response?.data || null);
    setPendingAddress(newAddress);
    setActiveModal(null);
    if (response?.data?.show_screen === true) {
      setShowClusterChangeModal(true);
    } else {
      await fetchAddressList(customerId, oldAddressId, newAddressId, mappedClinicId);
      await refetchCartDetails(
        activeCartId,
        mappedClinicId,
        "hd",
        cartLevelCouponId,
        newAddress?.pincode,
        newAddress?.address_id,
      );
      callLogAPILabSelectedAddress(newAddressId);
    }
  };

  const handleConfirmClusterChange = async () => {
    try {
      if (!currentAddress || !pendingAddress) {
        toast.error("No address selected.");
        return;
      }
      const oldAddressId = currentAddress?.address_id;
      const newAddressId = pendingAddress?.address_id;
      const res = await fetchAddressList(customerId, oldAddressId, newAddressId, mappedClinicId);
      if (res && res.length > 0) {
        setCurrentAddress(pendingAddress);
        await refetchCartDetails(activeCartId, mappedClinicId, "hd", cartLevelCouponId, pendingAddress?.pincode);
        toast.success("Address updated successfully.");
      } else {
        toast.error("Failed to update address. Try again.");
      }
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong.");
    } finally {
      setPendingAddress(null);
    }
  };

  const handleCloseClusterChangeModal = () => {
    setShowClusterChangeModal(false);
  };

  const handleSetPendingAddressAndNewAddress = (newAddress: any) => {
    setPendingAddress(newAddress);
    if (!newAddress) return;
    setSelectedAddressId(newAddress.address_id);
    setSelectedAddress(newAddress);
    localStorage.setItem(`selectedAddress_${customerId}`, JSON.stringify(newAddress));
    setActiveModal(null);
  };

  const isBillable = activeCartDetails?.disable_checkout;
  const actionButtonBase =
    "inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-md border px-2.5 text-xs font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/30";
  const actionButtonActive = "border-blue-600 bg-blue-600 text-white hover:bg-blue-700";
  const actionButtonInactive = "border-blue-200 bg-white text-[#0068FF] hover:border-blue-300 hover:bg-blue-50";

  const { handlePresent, openMirrorScreen, closeMirrorScreen, isPresentActive } = useCartMirrorPresentation({
    userEmailId,
    activeCartId,
    customerId,
    customerDetails,
    activeCartDetails,
    mappedClinicId,
    petDetails,
    fetchCustomerAPI,
    fetchMemberships,
    fetchPitchDetails,
    isMirrorActionVisible,
    setIsMirrorActionVisible,
    setRecentEMRData,
    setMembershipData,
    setPitchingData,
    initialRender,
    setInitialRender,
    initialCartLoad,
    setInitialCartLoad,
  });

  return (
    <>
      <div className="w-full h-[calc(100vh-160px)] min-h-[420px] flex flex-col cart-screen-root">
        <CartHeaderActionsBar
          paravetSlot={
            shouldShowParavetSection && (
              <CartParavetSelectCard
                paravetList={paravetList}
                selectedParavetId={selectedParavetId}
                onSelectParavet={(paravetId) => {
                  setSelectedParavetId(paravetId);
                  logCartAction("CART_PARAVET_SELECTED", { staff_id: paravetId });
                }}
                isLoading={isParavetListLoading}
              />
            )
          }
          onRaiseExemption={handleOpenExemptionModal}
          checkoutGateActions={
            <>
              <CartJobCardEligibilityCard
                jobCardEligibility={jobCardEligibility}
                onUploadJobCard={handleOpenJobCardUploadModal}
                buttonClassName={actionButtonBase}
              />
              {shouldShowCartPinSection && (
                <CartUserPinCard
                  cartUserPinDigits={cartUserPinDigits}
                  cartPinInputRefs={cartPinInputRefs}
                  isCartPinComplete={isCartPinComplete}
                  isCartPinVerifying={isCartPinVerifying}
                  isCartPinCleared={isCartPinGateCleared}
                  onCartPinChange={handleCartPinChange}
                  onCartPinKeyDown={handleCartPinKeyDown}
                  onVerifyCartPin={handleVerifyCartPin}
                  onSendCartPin={handleSendCartPin}
                  buttonClassName={actionButtonBase}
                />
              )}
            </>
          }
          loader={loader}
          actionButtonBase={actionButtonBase}
          actionButtonActive={actionButtonActive}
          actionButtonInactive={actionButtonInactive}
          onRefreshCart={handelRefreschCartItems}
          showEmrButton={Boolean(activeCartDetails?.emr_number && activeCartDetails?.emr_id)}
          onViewEmr={() => viewEMRPDF(activeCartDetails?.emr_id)}
          onPrintCart={handlePrintCart}
          isMirrorActionVisible={isMirrorActionVisible}
          isPresentActive={isPresentActive}
          onPresent={() => handlePresent("PRESENT")}
          onOpenMirrorScreen={openMirrorScreen}
        />
        <div ref={printContainerRef} className="flex-1 min-h-0 cart-print-root">
          <div className="cart-print-only mb-3">
            <h1 className="text-base font-semibold text-black">Cart Summary</h1>
            <div className="text-xs text-gray-600">
              <p>
                <span className="font-semibold text-black">Clinic:</span> {mappedClinicName || "-"}
              </p>
              <p>
                <span className="font-semibold text-black">Customer Name:</span> {customerDetails?.name || "-"}
              </p>
            </div>
          </div>
          <div className="grid h-full grid-cols-12 cart-print-grid">
            <div className="col-span-9 flex min-h-0 flex-col bg-white shadow-sm cart-print-main">
              {isMirrorActionVisible ? (
                <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
                <MirrorScreenActions
                  petDetails={petDetails}
                  emrData={recentEMRData}
                  cartId={activeCartId}
                  customerId={customerId}
                  onClose={closeMirrorScreen}
                  membershipList={membershipData}
                  pitchingList={pitchingData}
                />
                </div>
              ) : (
                <>
                  <div className="shrink-0 cart-print-hide">
                    <CartCreationHeaderComponent
                      activeCartId={activeCartId}
                      clinicId={mappedClinicId}
                      fetchLotDetails={fetchLotDetails}
                      cartLevelCouponId={cartLevelCouponId}
                      handleApplyCartLevelCoupon={handleApplyCartLevelCoupon}
                      handleRemoveCartLevelCoupon={handleRemoveCartLevelCoupon}
                      currentAddress={currentAddress}
                    />
                  </div>
                  <CartCreationTableComponent
                    alreadyAddedURIDError={alreadyAddedURIDError}
                    cartLevelCouponId={cartLevelCouponId}
                    fetchLotDetails={fetchLotDetails}
                    selectedAddress={selectedAddress}
                    currentAddress={currentAddress}
                  />
                  <CartPriceBreakupPanel
                    activeCartDetails={activeCartDetails}
                    customerName={customerDetails?.name ?? ""}
                    availableHdItems={availableHdItems}
                    selectedDeliveryAddresses={currentAddress ? [currentAddress] : []}
                    addressList={addressList}
                    onChangeAddress={() => {
                      logCartAction("CART_CHANGE_ADDRESS_CLICK", { address_id: currentAddress?.address_id });
                      setActiveModal("addressList");
                    }}
                    onAddAddress={() => {
                      logCartAction("CART_ADD_ADDRESS_CLICK");
                      setActiveModal("addAddress");
                    }}
                    onViewDetails={() => logCartAction("CART_VIEW_PRICE_DETAILS_CLICK")}
                    isCartTabeLoading={isCartTabeLoading}
                    isHDCartTabeLoading={isHDCartTabeLoading}
                    checkOutLoader={checkOutLoader}
                    finalCheckoutDisabled={finalCheckoutDisabled}
                    isBillable={isBillable}
                    isParavetMissing={shouldShowParavetSection && !selectedParavetId}
                    onCheckout={handleCheckoutmodal}
                  />
                </>
              )}
            </div>
            <div
              className={`col-span-3 flex min-h-0 flex-col bg-gray-50 w-full pl-2 cart-print-sidebar ${
                isMirrorActionVisible ? "blur-sm opacity-60 pointer-events-none select-none" : ""
              }`}
            >
              <div className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain cart-print-hide">
                {checkoutNudge && (
                  <NudgesContainer
                    source="Cart"
                    alert_category_list={alert_category_list}
                    cart_id={activeCartId}
                    handelCheckoutFunction={handelCheckoutFunction}
                    setCheckoutNudge={setCheckoutNudge}
                    checkoutNudge={checkoutNudge}
                    pet_Id={petId}
                  />
                )}

                <CartStockIncompleteComponent stockOutItems={outOfStockItems} selectedAddress={selectedAddress} />
                <CartControlCenter
                  cartId={activeCartId}
                  clinicId={mappedClinicId}
                  customerId={customerId}
                  addItemToCart={fetchLotDetails}
                  cartPets={activeCartDetails?.pets}
                  commentsContent={
                    <CartCommentsCard
                      comments={comments}
                      personaDiscountComments={personaDiscountComments}
                      veticDateFormat={veticDateFormat}
                    />
                  }
                />
              </div>
            </div>
          </div>
        </div>

        <CartModalsCluster
          openMembershipExemption={openMembershipExemption}
          setOpenMembershipExemption={setOpenMembershipExemption}
          customerName={customerDetails?.name ?? ""}
          customerId={customerId}
          mappedClinicId={mappedClinicId}
          onExemptionRefresh={handleExemptionRefresh}
          openCartPinExemptionModal={openCartPinExemptionModal}
          setOpenCartPinExemptionModal={setOpenCartPinExemptionModal}
          selectedPinBookingId={selectedPinBookingId}
          onCartPinExemptionSuccess={async () => {
            setIsCartPinExempted(true);
            setIsCartPinVerified(false);
            toast.success("Exemption marked successfully");
            if (activeCartId) {
              await refetchCartDetails(
                activeCartId,
                mappedClinicId,
                "normal",
                cartLevelCouponId,
                currentAddress?.pincode,
                undefined,
                true,
              );
            }
          }}
          isCartPinSendModalOpen={isCartPinSendModalOpen}
          setIsCartPinSendModalOpen={setIsCartPinSendModalOpen}
          customerMobileNumber={customerDetails?.mobile}
          onCartPinSendSuccess={handleCartPinSendSuccess}
          checkoutModalOpen={open}
          setCheckoutModalOpen={setOpen}
          currentAddress={currentAddress}
          activeCartDetails={activeCartDetails}
          activeCartId={activeCartId}
          availableHdItems={availableHdItems}
          handlePresent={handlePresent}
          callLogAPIBase={callLogAPIBase}
          setActiveModal={setActiveModal}
          isBarcodeSlideoverOpen={isBarcodeSlideoverOpen}
          barcodeItemName={pendingBarcodeValidation?.item?.name}
          barcodeInputValue={barcodeInputValue}
          showBarcodeMismatchError={showBarcodeMismatchError}
          onChangeBarcode={(value) => {
            setBarcodeInputValue(value);
            if (showBarcodeMismatchError) {
              setShowBarcodeMismatchError(false);
            }
          }}
          onCloseBarcodeValidation={closeBarcodeValidationSlideover}
          onConfirmBarcodeValidation={handleBarcodeValidateAndAdd}
          activeModal={activeModal}
          selectedAddressId={selectedAddressId}
          addressList={addressList}
          onCloseActiveModal={handleSetActiveModal}
          onAddressAdded={handleSetPendingAddressAndNewAddress}
          onSelectAddress={handleAddressChange}
          onOpenAddAddressFromList={(addressId?: string) => {
            setSelectedAddressId(addressId || null);
            setActiveModal("addAddress");
          }}
          selectedParavetId={selectedParavetId}
        />
      </div>
      <PetUpdateModal
        isOpen={isPetModelOpen}
        onClose={() => setIsPetModelOpen(false)}
        errorSource={errorSource}
        onSuccess={async () => {
          const response = await retry();
          if (response?.payload) {
            await handlePetUpdateRetrySuccess(
              {
                id: response.payload.id,
                tag: response.payload.selected_item,
                price: response.payload.unit_price,
                stock_qty_with_text: response.payload.availability,
                is_hd_stock: response.payload.is_hd_stock,
                is_deworming_item: response.payload.is_deworming_item,
                pet_id: response.payload.pet_id,
              } as any,
              response.result,
            );

            if (activeCartId && mappedClinicId) {
              await refetchCartDetails(
                activeCartId,
                mappedClinicId,
                "normal",
                cartLevelCouponId,
                currentAddress?.pincode,
                undefined,
                true,
              );
            }

            refetchCartList();
          }
        }}
      />

      <CartDetailsModal
        isOpen={isCartDetailsModalOpen}
        onClose={() => setIsCartDetailsModalOpen(false)}
        onContinue={handleCartDetailsContinue}
        customerName={customerDetails?.name ?? ""}
        activeCartDetails={activeCartDetails}
        hdItems={availableHdItems}
        rewardCreditBalance={rewardCreditBalance}
        useRewardCredit={useRewardCredit}
        onUseRewardCreditChange={setUseRewardCredit}
      />

      {isFollowUpModalOpen && (
        <FollowupBookingCreation
          open={isFollowUpModalOpen}
          onClose={() => setIsFollowUpModalOpen(false)}
          onCrossClose={() => handlePresent("PRESENT")}
          cmbookingRecommendations={cmbookingRecommendations}
          userId={customerId}
          hideCannotCreateBooking
        />
      )}

      {showClusterChangeModal && (
        <AddressClusterChangeModal
          isOpen={showClusterChangeModal}
          onClose={handleCloseClusterChangeModal}
          setActiveModal={setActiveModal}
          activeCartDetails={activeCartDetails}
          onConfirm={handleConfirmClusterChange}
          pendingAddress={pendingAddress}
        />
      )}
    </>
  );
};
export default CartCreationComponent;
